import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './Organizations.css';
import {
  FaBuilding,
  FaUsers,
  FaSpinner,
  FaDollarSign,
  FaCalendarAlt,
  FaEdit,
  FaSave,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaTimesCircle,
  FaLink,
  FaCopy,
  FaKey,
  FaExternalLinkAlt,
  FaImage,
  FaTrash,
  FaPlus,
  FaGlobe,
} from 'react-icons/fa';
import dayjs from 'dayjs';

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMarkup, setEditingMarkup] = useState(null);
  const [newMarkupValue, setNewMarkupValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [userMap, setUserMap] = useState({});

  // Add organization state
  const [showAddOrgForm, setShowAddOrgForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgMarkup, setNewOrgMarkup] = useState('0');
  const [creatingOrg, setCreatingOrg] = useState(false);
  
  // Signup link generator state
  const [viewMode, setViewMode] = useState('organizations'); // 'organizations' | 'signup-links'
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [expirationDays, setExpirationDays] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Publisher and sign selection state
  const [publishers, setPublishers] = useState([]);
  const [selectedPublisher, setSelectedPublisher] = useState('');
  const [availableSigns, setAvailableSigns] = useState([]);
  const [selectedSignKeys, setSelectedSignKeys] = useState([]);
  const [loadingPublishers, setLoadingPublishers] = useState(false);
  const [loadingSigns, setLoadingSigns] = useState(false);
  const [websiteBuilderSavingId, setWebsiteBuilderSavingId] = useState(null);
  const [accountLookup, setAccountLookup] = useState('');

  useEffect(() => {
    fetchOrganizations();
    loadPublishers();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      // Fetch organizations
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch all users for mapping
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersMap = {};
      usersSnapshot.docs.forEach(doc => {
        usersMap[doc.id] = { id: doc.id, ...doc.data() };
      });

      setUserMap(usersMap);
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load publishers from API
  const loadPublishers = async () => {
    setLoadingPublishers(true);
    try {
      const baseURL = 'https://api.vuebillboards.com';
      const response = await fetch(`${baseURL}/api/publishers`);
      const result = await response.json();
      
      if (result.success) {
        setPublishers(result.publishers || []);
        console.log('Loaded publishers:', result.publishers?.length);
      }
    } catch (err) {
      console.error('Error loading publishers:', err);
      setError('Failed to load publishers');
    } finally {
      setLoadingPublishers(false);
    }
  };

  // Load signs for selected publisher
  const loadPublisherSigns = async (publisherName) => {
    if (!publisherName) {
      setAvailableSigns([]);
      return;
    }
    
    setLoadingSigns(true);
    try {
      const baseURL = 'https://api.vuebillboards.com';
      const response = await fetch(`${baseURL}/api/publisher/sign-keys?publisher=${encodeURIComponent(publisherName)}`);
      const result = await response.json();
      
      if (result.success) {
        setAvailableSigns(result.signKeys || []);
        console.log('Loaded signs for publisher:', result.signKeys?.length);
      }
    } catch (err) {
      console.error('Error loading signs:', err);
      setError('Failed to load signs for publisher');
    } finally {
      setLoadingSigns(false);
    }
  };

  // Handle publisher change
  const handlePublisherChange = (e) => {
    const publisher = e.target.value;
    setSelectedPublisher(publisher);
    setSelectedSignKeys([]);
    setAvailableSigns([]);
    if (publisher) {
      loadPublisherSigns(publisher);
    }
  };

  // Toggle sign selection
  const toggleSignSelection = (signKey) => {
    setSelectedSignKeys(prev => {
      if (prev.includes(signKey)) {
        return prev.filter(key => key !== signKey);
      } else {
        return [...prev, signKey];
      }
    });
  };

  // Select all signs
  const selectAllSigns = () => {
    setSelectedSignKeys([...availableSigns]);
  };

  // Deselect all signs
  const deselectAllSigns = () => {
    setSelectedSignKeys([]);
  };

  // Reset completed signup (for testing)
  const resetCompletedSignup = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to reset the signup for "${orgName}"? This will clear all signup data and allow the organization to be used again.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');

      // Clear all signup-related fields
      await updateDoc(doc(db, 'organizations', orgId), {
        status: 'pending',
        adminUserId: null,
        userName: null,
        userEmail: null,
        firstSignupCompletedAt: null,
        signupLinkEnabled: false,
        securityPin: null,
        signupUrl: null,
        signupLinkCreatedAt: null,
        signupLinkExpiresAt: null,
        updatedAt: new Date()
      });

      setSuccess(`Successfully reset signup for "${orgName}"`);
      
      // Reload organizations
      await fetchOrganizations();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error resetting signup:', err);
      setError('Failed to reset signup: ' + err.message);
    }
  };

  const resetAddOrgForm = () => {
    setNewOrgName('');
    setNewOrgMarkup('0');
    setShowAddOrgForm(false);
  };

  const createOrganization = async () => {
    const companyName = newOrgName.trim();
    if (!companyName) {
      setError('Please enter an organization name');
      return;
    }

    const markupValue = parseFloat(newOrgMarkup);
    if (newOrgMarkup !== '' && (Number.isNaN(markupValue) || markupValue < 0)) {
      setError('Please enter a valid markup percentage');
      return;
    }

    setCreatingOrg(true);
    setError('');
    setSuccess('');

    try {
      const baseURL = 'https://api.vuebillboards.com';
      const response = await fetch(`${baseURL}/api/organizations/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companyName }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to create organization');
        return;
      }

      if (!Number.isNaN(markupValue) && markupValue !== 0) {
        await updateDoc(doc(db, 'organizations', result.organizationId), {
          markup: markupValue,
          updatedAt: new Date(),
        });
      }

      setSuccess(`Organization "${companyName}" created (${result.organizationId})`);
      resetAddOrgForm();
      await fetchOrganizations();

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization: ' + err.message);
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleEditMarkup = (org) => {
    setEditingMarkup(org.id);
    setNewMarkupValue(org.markup?.toString() || '0');
  };

  const handleCancelEdit = () => {
    setEditingMarkup(null);
    setNewMarkupValue('');
  };

  const handleSaveMarkup = async (orgId) => {
    if (!newMarkupValue || isNaN(newMarkupValue)) {
      alert('Please enter a valid number for markup');
      return;
    }

    setSaving(true);
    try {
      const markupValue = parseFloat(newMarkupValue);
      await updateDoc(doc(db, 'organizations', orgId), {
        markup: markupValue,
        updatedAt: new Date()
      });

      // Update local state
      setOrganizations(prev => prev.map(org => 
        org.id === orgId 
          ? { ...org, markup: markupValue, updatedAt: new Date() }
          : org
      ));

      setEditingMarkup(null);
      setNewMarkupValue('');
    } catch (error) {
      console.error('Error updating markup:', error);
      alert('Error updating markup. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return dayjs(date).format('MMM D, YYYY [at] h:mm A');
  };

  const getUserInfo = (userId) => {
    const user = userMap[userId];
    if (!user) return { name: 'Unknown User', email: 'N/A', phoneNumber: 'N/A' };
    
    return {
      name: user.name || user.displayName || 'No Name',
      email: user.email || 'No Email',
      phoneNumber: user.phoneNumber || 'No Phone'
    };
  };

  const organizationForUser = (user) => {
    if (!user?.id) return null;
    const linkedOrgId = String(user.organizationId || user.orgId || '').trim();
    if (linkedOrgId) {
      const linked = organizations.find((org) => org.id === linkedOrgId);
      if (linked) return linked;
    }
    return organizations.find((org) =>
      org.ownerId === user.id ||
      org.adminUserId === user.id ||
      (Array.isArray(org.members) && org.members.includes(user.id))
    ) || null;
  };

  const setWebsiteBuilderEnabled = async ({ collectionName, docId, enabled, label }) => {
    if (!enabled) {
      const confirmed = window.confirm(
        `Turn off the website builder for "${label}"? The website studio stays closed until it is enabled again. Map embeds stay available.`
      );
      if (!confirmed) return;
    }

    setWebsiteBuilderSavingId(docId);
    setError('');
    setSuccess('');
    try {
      await updateDoc(doc(db, collectionName, docId), {
        websiteBuilderEnabled: enabled,
        updatedAt: new Date(),
      });

      if (collectionName === 'organizations') {
        setOrganizations((prev) =>
          prev.map((org) =>
            org.id === docId
              ? { ...org, websiteBuilderEnabled: enabled, updatedAt: new Date() }
              : org
          )
        );
      } else {
        setUserMap((prev) => ({
          ...prev,
          [docId]: {
            ...(prev[docId] || { id: docId }),
            id: docId,
            websiteBuilderEnabled: enabled,
            updatedAt: new Date(),
          },
        }));
      }

      setSuccess(
        enabled
          ? `Website builder enabled for ${label}`
          : `Website builder turned off for ${label}`
      );
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating website builder:', err);
      setError('Failed to update website builder: ' + err.message);
    } finally {
      setWebsiteBuilderSavingId(null);
    }
  };

  const renderWebsiteBuilderDetails = (record) => {
    const enabled = record?.websiteBuilderEnabled === true;
    return (
      <>
        <div className="website-builder-status-row">
          <span className={`status-badge ${enabled ? 'builder-on' : 'inactive'}`}>
            {enabled ? <FaCheckCircle /> : <FaTimesCircle />}
            {enabled ? 'Enabled' : 'Off'}
          </span>
        </div>
        {record?.websiteBuilderRequestedAt && (
          <div className="website-builder-request">
            <div><strong>Requested:</strong> {formatDate(record.websiteBuilderRequestedAt)}</div>
            <div><strong>By:</strong> {record.websiteBuilderRequestedBy || 'Unknown'}</div>
            {record.websiteBuilderRequestedByEmail && (
              <div><strong>Email:</strong> {record.websiteBuilderRequestedByEmail}</div>
            )}
          </div>
        )}
      </>
    );
  };

  const renderWebsiteBuilderButton = (collectionName, docId, record, label) => {
    const enabled = record?.websiteBuilderEnabled === true;
    const savingThis = websiteBuilderSavingId === docId;
    return (
      <button
        type="button"
        className={enabled ? 'website-builder-off-btn' : 'website-builder-on-btn'}
        disabled={savingThis}
        onClick={() =>
          setWebsiteBuilderEnabled({
            collectionName,
            docId,
            enabled: !enabled,
            label,
          })
        }
      >
        {savingThis ? 'Saving…' : enabled ? 'Turn off website builder' : 'Enable website builder'}
      </button>
    );
  };

  // Filter organizations for signup link generation
  const getAvailableOrganizations = () => {
    return organizations.filter(org => {
      // Show organizations that either don't have admin user or link expired
      return !org.adminUserId || org.status !== 'active';
    });
  };

  const getActiveSignupLinks = () => {
    return organizations.filter(org => {
      return org.signupLinkEnabled && org.status === 'pending';
    });
  };

  const getCompletedSignups = () => {
    return organizations.filter(org => {
      return org.status === 'active' && org.adminUserId;
    });
  };

  // Check if link is expired
  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    
    let date;
    if (expiresAt.toDate) {
      date = expiresAt.toDate();
    } else if (typeof expiresAt === 'string') {
      date = new Date(expiresAt);
    } else {
      return false;
    }
    
    return date < new Date();
  };

  // Get organization name by ID
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId;
  };

  // Logo upload functions
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('File size exceeds 5MB. Please choose a smaller file.');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const uploadLogo = async (orgId) => {
    if (!logoFile) return null;

    setUploadingLogo(true);
    try {
      // Get file extension
      const fileExt = logoFile.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `organization-logos/${orgId}_${timestamp}.${fileExt}`;
      
      // Create storage reference
      const storageRef = ref(storage, fileName);
      
      // Upload file
      await uploadBytes(storageRef, logoFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update organization document with logo URL
      await updateDoc(doc(db, 'organizations', orgId), {
        logoUrl: downloadURL,
        logoUpdatedAt: new Date()
      });
      
      return downloadURL;
    } catch (err) {
      console.error('Error uploading logo:', err);
      throw new Error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // API functions
  const generateSignupLink = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }
    
    // Validate publisher and signs if selected
    if (selectedPublisher && selectedSignKeys.length === 0) {
      setError('Please select at least one sign for the selected publisher');
      return;
    }
    
    setGenerating(true);
    setError('');
    setSuccess('');
    setGeneratedLink(null);
    
    try {
      // Upload logo first if one is selected
      let logoUrl = null;
      if (logoFile) {
        try {
          logoUrl = await uploadLogo(selectedOrgId);
          setSuccess('Logo uploaded successfully! Configuring organization...');
        } catch (uploadErr) {
          setError('Failed to upload logo. Please try again.');
          setGenerating(false);
          return;
        }
      }
      
      // Update organization with logo, publisher, and signs
      const orgUpdate = {
        updatedAt: new Date()
      };
      
      if (logoUrl) {
        orgUpdate.logoUrl = logoUrl;
      }
      
      if (selectedPublisher) {
        orgUpdate.publisherName = selectedPublisher;
        orgUpdate.publisherMode = true;
        orgUpdate.ownedSigns = selectedSignKeys;
        console.log('Setting publisher:', selectedPublisher, 'with', selectedSignKeys.length, 'signs');
      }
      
      if (Object.keys(orgUpdate).length > 1) { // More than just updatedAt
        await updateDoc(doc(db, 'organizations', selectedOrgId), orgUpdate);
        console.log('Organization updated with:', Object.keys(orgUpdate).join(', '));
        setSuccess('Organization configured! Generating signup link...');
      }
      
      const baseURL = 'https://api.vuebillboards.com';
      
      const response = await fetch(`${baseURL}/api/organizations/generate-signup-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organizationId: selectedOrgId,
          expirationDays: expirationDays
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        setError(result.error || 'Failed to generate signup link');
        return;
      }
      
      setGeneratedLink(result);
      setSuccess(logoUrl || selectedPublisher 
        ? 'Signup link generated successfully with configuration!' 
        : 'Signup link generated successfully!');
      
      // Reload organizations to update list
      await fetchOrganizations();
      
      // Reset form
      setSelectedOrgId('');
      setExpirationDays(30);
      setSelectedPublisher('');
      setSelectedSignKeys([]);
      setAvailableSigns([]);
      setLogoFile(null);
      setLogoPreview(null);
      
    } catch (err) {
      console.error('Error generating signup link:', err);
      setError('Failed to generate signup link: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const pendingWebsiteBuilderOrgs = organizations.filter(
    (org) => org.websiteBuilderRequestedAt && org.websiteBuilderEnabled !== true
  );

  const standaloneWebsiteBuilderAccounts = Object.values(userMap).filter((user) => {
    if (!user?.id) return false;
    if (organizationForUser(user)) return false;
    return Boolean(user.websiteBuilderRequestedAt) || user.websiteBuilderEnabled === true;
  });

  const lookupQuery = accountLookup.trim().toLowerCase();
  const lookedUpUser = lookupQuery
    ? Object.values(userMap).find(
        (user) =>
          user.id === accountLookup.trim() ||
          String(user.email || '').toLowerCase() === lookupQuery
      ) || null
    : null;
  const lookedUpOrg = lookedUpUser ? organizationForUser(lookedUpUser) : null;

  if (loading) {
    return (
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 rounded-xl border border-slate-200/80 bg-white/90 p-12 shadow-sm">
          <FaSpinner className="fa-spin text-3xl text-primary" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Loading organizations…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
      <div className="f-header mb-6 flex flex-col gap-4 rounded-xl border border-slate-200/80 shadow-sm md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="f-title flex items-center gap-2">
            <FaBuilding className="shrink-0 text-primary" aria-hidden />
            Organizations
          </h1>
          <p className="f-subtitle mb-0">Manage sign owners and their organizations</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`inline-flex items-center gap-2 ${viewMode === 'organizations' ? 'f-button' : 'f-button f-button-secondary'}`}
            onClick={() => setViewMode('organizations')}
          >
            <FaBuilding aria-hidden />
            Organizations
          </button>
          <button
            type="button"
            className={`inline-flex items-center gap-2 ${viewMode === 'signup-links' ? 'f-button' : 'f-button f-button-secondary'}`}
            onClick={() => setViewMode('signup-links')}
          >
            <FaLink aria-hidden />
            Signup Links
          </button>
          {viewMode === 'organizations' && (
            <button
              type="button"
              className="f-button inline-flex items-center gap-2"
              onClick={() => {
                setShowAddOrgForm((prev) => !prev);
                setError('');
              }}
            >
              <FaPlus aria-hidden />
              {showAddOrgForm ? 'Cancel' : 'Add Organization'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
          role="status"
        >
          {success}
        </div>
      )}

      <div className="organizations-container space-y-6">
        {viewMode === 'organizations' ? (
          <>
            {showAddOrgForm && (
              <div className="create-form-card">
                <h3 className="mb-4 text-lg font-semibold text-slate-800">Add Organization</h3>
                <div className="form-group">
                  <label htmlFor="new-org-name">Organization Name</label>
                  <input
                    id="new-org-name"
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="Enter company / organization name"
                    className="form-control"
                    disabled={creatingOrg}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-org-markup">Markup % (Optional)</label>
                  <input
                    id="new-org-markup"
                    type="number"
                    value={newOrgMarkup}
                    onChange={(e) => setNewOrgMarkup(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="100"
                    className="form-control"
                    disabled={creatingOrg}
                  />
                </div>
                <button
                  type="button"
                  onClick={createOrganization}
                  disabled={!newOrgName.trim() || creatingOrg}
                  className="btn btn-success"
                >
                  {creatingOrg ? 'Creating…' : 'Create Organization'}
                </button>
              </div>
            )}

            <div className="website-builder-panel">
              <h3><FaGlobe /> Website builder</h3>
              <p>
                Sign owners request access from the Embeds tab. That saves the request on their organization. Enable it here to open the website studio.
              </p>

              <div className="website-builder-panel-block">
                <h4>Requests ({pendingWebsiteBuilderOrgs.length})</h4>
                {pendingWebsiteBuilderOrgs.length === 0 ? (
                  <p className="website-builder-empty">No pending website builder requests.</p>
                ) : (
                  <div className="website-builder-request-list">
                    {pendingWebsiteBuilderOrgs.map((org) => (
                      <div key={org.id} className="website-builder-request-row">
                        <div>
                          <strong>{org.name || org.id}</strong>
                          <span className="org-id">Org: {org.id}</span>
                          {renderWebsiteBuilderDetails(org)}
                        </div>
                        {renderWebsiteBuilderButton('organizations', org.id, org, org.name || org.id)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="website-builder-panel-block">
                <h4>Accounts without an organization ({standaloneWebsiteBuilderAccounts.length})</h4>
                <p className="website-builder-note">
                  If the account has no organization, the flag is stored on the user instead of an organization document.
                </p>
                {standaloneWebsiteBuilderAccounts.length === 0 ? (
                  <p className="website-builder-empty">No standalone accounts have requested or enabled the website builder.</p>
                ) : (
                  <div className="website-builder-request-list">
                    {standaloneWebsiteBuilderAccounts.map((user) => {
                      const info = getUserInfo(user.id);
                      const label = info.name !== 'Unknown User' && info.name !== 'No Name' ? info.name : (user.email || user.id);
                      return (
                        <div key={user.id} className="website-builder-request-row">
                          <div>
                            <strong>{label}</strong>
                            <span className="org-id">User: {user.id}</span>
                            {renderWebsiteBuilderDetails(user)}
                          </div>
                          {renderWebsiteBuilderButton('users', user.id, user, label)}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="form-group website-builder-lookup">
                  <label htmlFor="website-builder-account-lookup">Enable a user with no organization</label>
                  <input
                    id="website-builder-account-lookup"
                    type="text"
                    className="form-control"
                    value={accountLookup}
                    onChange={(e) => setAccountLookup(e.target.value)}
                    placeholder="User email or user id"
                  />
                  {lookupQuery && !lookedUpUser && (
                    <p className="form-hint">No user matches that email or id.</p>
                  )}
                  {lookedUpUser && lookedUpOrg && (
                    <p className="form-hint">
                      This account belongs to {lookedUpOrg.name || lookedUpOrg.id}. Enable the website builder on that organization (Org: {lookedUpOrg.id}).
                    </p>
                  )}
                  {lookedUpUser && !lookedUpOrg && standaloneWebsiteBuilderAccounts.some((user) => user.id === lookedUpUser.id) && (
                    <p className="form-hint">This account is listed above.</p>
                  )}
                  {lookedUpUser && !lookedUpOrg && !standaloneWebsiteBuilderAccounts.some((user) => user.id === lookedUpUser.id) && (
                    <div className="website-builder-request-row">
                      <div>
                        <strong>{lookedUpUser.name || lookedUpUser.email || lookedUpUser.id}</strong>
                        <span className="org-id">User: {lookedUpUser.id}</span>
                        {renderWebsiteBuilderDetails(lookedUpUser)}
                      </div>
                      {renderWebsiteBuilderButton(
                        'users',
                        lookedUpUser.id,
                        lookedUpUser,
                        lookedUpUser.name || lookedUpUser.email || lookedUpUser.id
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="organizations-grid">
          {organizations.map((org) => {
            const ownerInfo = getUserInfo(org.ownerId);
            const isEditing = editingMarkup === org.id;

            return (
              <div key={org.id} className="organization-card">
                <div className="org-header">
                  <div className="org-title">
                    <h3>{org.name}</h3>
                    <span className="org-id">ID: {org.id}</span>
                  </div>
                  <div className="org-status">
                    {org.isActive ? (
                      <span className="status-badge active">
                        <FaCheckCircle /> Active
                      </span>
                    ) : (
                      <span className="status-badge inactive">
                        <FaTimesCircle /> Inactive
                      </span>
                    )}
                    {org.websiteBuilderEnabled === true ? (
                      <span className="status-badge builder-on">
                        <FaGlobe /> Website builder
                      </span>
                    ) : org.websiteBuilderRequestedAt ? (
                      <span className="status-badge builder-requested">
                        Builder requested
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="org-content">
                  <div className="org-stats">
                    <div className="stat-item">
                      <FaUsers />
                      <span>{org.memberCount || 0} Members</span>
                    </div>
                    <div className="stat-item">
                      <FaDollarSign />
                      <span>Markup: {org.markup || 0}%</span>
                    </div>
                  </div>

                  <div className="org-dates">
                    <div className="date-item">
                      <FaCalendarAlt />
                      <span>Created: {formatDate(org.createdAt)}</span>
                    </div>
                    <div className="date-item">
                      <FaCalendarAlt />
                      <span>Updated: {formatDate(org.updatedAt)}</span>
                    </div>
                  </div>

                  <div className="owner-section">
                    <h4><FaUser /> Sign Owner</h4>
                    <div className="owner-info">
                      <div className="owner-detail">
                        <strong>Name:</strong> {ownerInfo.name}
                      </div>
                      <div className="owner-detail">
                        <FaEnvelope />
                        <span>{ownerInfo.email}</span>
                      </div>
                      <div className="owner-detail">
                        <FaPhone />
                        <span>{ownerInfo.phoneNumber}</span>
                      </div>
                    </div>
                  </div>

                  <div className="website-builder-section">
                    <h4><FaGlobe /> Website builder</h4>
                    <p className="website-builder-note">
                      Map embeds stay available. The website studio stays closed until this is on.
                    </p>
                    {renderWebsiteBuilderDetails(org)}
                    {renderWebsiteBuilderButton('organizations', org.id, org, org.name || org.id)}
                  </div>

                  <div className="markup-section">
                    <h4><FaDollarSign /> Markup</h4>
                    {isEditing ? (
                      <div className="markup-edit">
                        <input
                          type="number"
                          value={newMarkupValue}
                          onChange={(e) => setNewMarkupValue(e.target.value)}
                          placeholder="Enter markup percentage"
                          min="0"
                          max="100"
                        />
                        <div className="edit-actions">
                          <button
                            className="save-btn"
                            onClick={() => handleSaveMarkup(org.id)}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : <FaSave />}
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={handleCancelEdit}
                            disabled={saving}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="markup-display">
                        <span className="markup-value">{org.markup || 0}%</span>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditMarkup(org)}
                        >
                          <FaEdit />
                        </button>
                      </div>
                    )}
                  </div>

                  {org.members && org.members.length > 0 && (
                    <div className="members-section">
                      <h4><FaUsers /> Members</h4>
                      <div className="members-list">
                        {org.members.map((memberId, index) => {
                          const memberInfo = getUserInfo(memberId);
                          return (
                            <div key={memberId} className="member-item">
                              <span className="member-name">{memberInfo.name}</span>
                              <span className="member-email">{memberInfo.email}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

            {organizations.length === 0 && (
              <div className="empty-state">
                <FaBuilding />
                <h3>No Organizations Found</h3>
                <p>There are no organizations in the system yet.</p>
                {!showAddOrgForm && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowAddOrgForm(true)}
                  >
                    <FaPlus /> Add Organization
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="signup-generator-section">
            {/* Generated Link Display */}
            {generatedLink && (
              <div className="generated-link-banner">
                <div className="banner-header">
                  <h3><FaCheckCircle /> Signup Link Generated</h3>
                  <button onClick={() => setGeneratedLink(null)} className="close-btn">×</button>
                </div>
                <div className="banner-body">
                  <div className="info-row">
                    <span className="label">Organization:</span>
                    <span className="value">{getOrgName(generatedLink.organizationId)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Security PIN:</span>
                    <span className="value pin-display">{generatedLink.securityPin}</span>
                    <button onClick={() => copyToClipboard(generatedLink.securityPin)} className="copy-button">
                      <FaCopy /> Copy
                    </button>
                  </div>
                  <div className="info-row">
                    <span className="label">Signup URL:</span>
                    <span className="value url-display">{generatedLink.signupUrl}</span>
                    <button onClick={() => copyToClipboard(generatedLink.signupUrl)} className="copy-button">
                      <FaCopy /> Copy
                    </button>
                  </div>
                  <div className="info-row">
                    <span className="label">Expires:</span>
                    <span className="value">{formatDate(generatedLink.expiresAt)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Create Link Form */}
            <div className="create-section">
              <button 
                onClick={() => setShowCreateForm(!showCreateForm)} 
                className="btn btn-primary"
              >
                {showCreateForm ? 'Hide Form' : '+ Generate New Signup Link'}
              </button>

              {showCreateForm && (
                <div className="create-form-card">
                  <div className="form-group">
                    <label>Select Organization</label>
                    <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} className="form-control">
                      <option value="">-- Select Organization --</option>
                      {getAvailableOrganizations().map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} ({org.id})
                        </option>
                      ))}
                    </select>
                    <p className="form-hint">
                      Organizations shown: {getAvailableOrganizations().length} available
                    </p>
                  </div>

                  <div className="form-group">
                    <label>Organization Logo (Optional)</label>
                    <div className="logo-upload-container">
                      {logoPreview ? (
                        <div className="logo-preview">
                          <img src={logoPreview} alt="Logo preview" />
                          <button 
                            onClick={removeLogo} 
                            className="remove-logo-btn"
                            type="button"
                          >
                            <FaTrash /> Remove
                          </button>
                        </div>
                      ) : (
                        <label className="logo-upload-label">
                          <FaImage />
                          <span>Upload Logo</span>
                          <input 
                            type="file" 
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={handleLogoChange}
                            className="logo-file-input"
                          />
                        </label>
                      )}
                    </div>
                    <p className="form-hint">
                      Supported formats: JPG, PNG, GIF, WebP (Max 5MB)
                    </p>
                  </div>

                  <div className="form-group">
                    <label>Publisher (Optional)</label>
                    <select 
                      value={selectedPublisher} 
                      onChange={handlePublisherChange}
                      className="form-control"
                      disabled={loadingPublishers}
                    >
                      <option value="">-- No Publisher (Manual Setup) --</option>
                      {publishers.map((pub) => (
                        <option key={pub} value={pub}>
                          {pub}
                        </option>
                      ))}
                    </select>
                    <p className="form-hint">
                      {loadingPublishers 
                        ? 'Loading publishers...' 
                        : `Select a publisher to pre-configure signs (${publishers.length} available)`}
                    </p>
                  </div>

                  {selectedPublisher && (
                    <div className="form-group">
                      <label>
                        Select Signs for {selectedPublisher}
                        <span className="sign-count">
                          ({selectedSignKeys.length} of {availableSigns.length} selected)
                        </span>
                      </label>
                      
                      {loadingSigns ? (
                        <div className="loading-signs">
                          <div className="spinner"></div>
                          <span>Loading signs...</span>
                        </div>
                      ) : availableSigns.length > 0 ? (
                        <div className="signs-selection">
                          <div className="signs-actions">
                            <button 
                              type="button" 
                              onClick={selectAllSigns} 
                              className="btn-small"
                              disabled={selectedSignKeys.length === availableSigns.length}
                            >
                              Select All
                            </button>
                            <button 
                              type="button" 
                              onClick={deselectAllSigns} 
                              className="btn-small"
                              disabled={selectedSignKeys.length === 0}
                            >
                              Deselect All
                            </button>
                          </div>
                          
                          <div className="signs-list">
                            {availableSigns.map((signKey) => (
                              <div 
                                key={signKey}
                                className={`sign-item ${selectedSignKeys.includes(signKey) ? 'selected' : ''}`}
                                onClick={() => toggleSignSelection(signKey)}
                              >
                                <input 
                                  type="checkbox" 
                                  checked={selectedSignKeys.includes(signKey)}
                                  onChange={() => toggleSignSelection(signKey)}
                                />
                                <span className="sign-key">{signKey}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="empty-signs">
                          <p>No signs available for this publisher</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Link Expiration (Days)</label>
                    <input 
                      value={expirationDays} 
                      onChange={(e) => setExpirationDays(Number(e.target.value))}
                      type="number" 
                      min="1" 
                      max="365"
                      className="form-control"
                    />
                    <p className="form-hint">
                      Default: 30 days
                    </p>
                  </div>

                  <button 
                    onClick={generateSignupLink} 
                    disabled={!selectedOrgId || generating || uploadingLogo}
                    className="btn btn-success"
                  >
                    {uploadingLogo ? 'Uploading Logo...' : generating ? 'Generating...' : 'Generate Signup Link'}
                  </button>
                </div>
              )}
            </div>

            {/* Active Signup Links */}
            <div className="links-section">
              <h3>Active Signup Links ({getActiveSignupLinks().length})</h3>
              
              {getActiveSignupLinks().length === 0 ? (
                <div className="empty-state">
                  <p>No active signup links</p>
                </div>
              ) : (
                <div className="signup-links-grid">
                  {getActiveSignupLinks().map((org) => (
                    <div 
                      key={org.id}
                      className={`link-info-card ${isExpired(org.signupLinkExpiresAt) ? 'expired' : ''}`}
                    >
                      <div className="link-card-header">
                        <div className="header-content">
                          {org.logoUrl && (
                            <img src={org.logoUrl} alt={`${org.name} logo`} className="org-logo-thumb" />
                          )}
                          <h4>{org.name}</h4>
                        </div>
                        <span 
                          className={`status-badge ${isExpired(org.signupLinkExpiresAt) ? 'expired' : 'active'}`}
                        >
                          {isExpired(org.signupLinkExpiresAt) ? 'Expired' : 'Active'}
                        </span>
                      </div>
                      <div className="link-card-body">
                        <div className="info-row">
                          <span className="label">Org ID:</span>
                          <span className="value">{org.id}</span>
                        </div>
                        {org.publisherName && (
                          <div className="info-row">
                            <span className="label">Publisher:</span>
                            <span className="value publisher-name">{org.publisherName}</span>
                          </div>
                        )}
                        {org.ownedSigns && org.ownedSigns.length > 0 && (
                          <div className="info-row">
                            <span className="label">Signs:</span>
                            <span className="value signs-count">{org.ownedSigns.length} sign{org.ownedSigns.length !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {org.ownedSigns && org.ownedSigns.length > 0 && (
                          <div className="signs-display">
                            {org.ownedSigns.map((signKey) => (
                              <span key={signKey} className="sign-badge">{signKey}</span>
                            ))}
                          </div>
                        )}
                        <div className="info-row">
                          <span className="label">PIN:</span>
                          <span className="value pin-display">{org.securityPin || 'N/A'}</span>
                          {org.securityPin && (
                            <button 
                              onClick={() => copyToClipboard(org.securityPin)} 
                              className="copy-button-small"
                            >
                              <FaCopy />
                            </button>
                          )}
                        </div>
                        <div className="info-row">
                          <span className="label">URL:</span>
                          <span className="value url-display-small">{org.signupUrl || 'N/A'}</span>
                          {org.signupUrl && (
                            <button 
                              onClick={() => copyToClipboard(org.signupUrl)} 
                              className="copy-button-small"
                            >
                              <FaCopy />
                            </button>
                          )}
                        </div>
                        <div className="info-row">
                          <span className="label">Created:</span>
                          <span className="value">{formatDate(org.signupLinkCreatedAt)}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Expires:</span>
                          <span className="value">{formatDate(org.signupLinkExpiresAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed Signups */}
            <div className="links-section">
              <h3>Completed Signups ({getCompletedSignups().length})</h3>
              
              {getCompletedSignups().length === 0 ? (
                <div className="empty-state">
                  <p>No completed signups yet</p>
                </div>
              ) : (
                <div className="signup-links-grid">
                  {getCompletedSignups().map((org) => (
                    <div 
                      key={org.id}
                      className="link-info-card completed"
                    >
                      <div className="link-card-header">
                        <div className="header-content">
                          {org.logoUrl && (
                            <img src={org.logoUrl} alt={`${org.name} logo`} className="org-logo-thumb" />
                          )}
                          <h4>{org.name}</h4>
                        </div>
                        <span className="status-badge completed">Completed</span>
                      </div>
                      <div className="link-card-body">
                        <div className="info-row">
                          <span className="label">Org ID:</span>
                          <span className="value">{org.id}</span>
                        </div>
                        {org.publisherName && (
                          <div className="info-row">
                            <span className="label">Publisher:</span>
                            <span className="value publisher-name">{org.publisherName}</span>
                          </div>
                        )}
                        {org.ownedSigns && org.ownedSigns.length > 0 && (
                          <div className="info-row">
                            <span className="label">Signs:</span>
                            <span className="value signs-count">{org.ownedSigns.length} sign{org.ownedSigns.length !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {org.ownedSigns && org.ownedSigns.length > 0 && (
                          <div className="signs-display">
                            {org.ownedSigns.map((signKey) => (
                              <span key={signKey} className="sign-badge">{signKey}</span>
                            ))}
                          </div>
                        )}
                        <div className="info-row">
                          <span className="label">Admin:</span>
                          <span className="value">{org.userName || 'N/A'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Email:</span>
                          <span className="value">{org.userEmail || 'N/A'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Completed:</span>
                          <span className="value">{formatDate(org.firstSignupCompletedAt)}</span>
                        </div>
                        <div className="info-row">
                          <button 
                            onClick={() => resetCompletedSignup(org.id, org.name)}
                            className="reset-signup-btn"
                          >
                            <FaTrash /> Reset Signup
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizations; 